import { createFileRoute } from '@tanstack/react-router';
import { getSubjectQueryOptions } from '@/api/queries/subjects';
import { ErrorComponent } from '@/components/generalError';
import { useSuspenseQuery } from '@tanstack/react-query';
import { BasicInformation } from '@/components/subjectDetails/basicInformation';
import { ProtocolDetails } from '@/components/subjectDetails/protocolDetails';
import { ConsentsTable } from '@/components/subjectDetails/consentsTable';
import { BackButton } from '@/components/ui/backButton';
import { useTranslation } from 'react-i18next';
import { SubjectStatus } from '@/components/subjectDetails/subjectStatus';
import { DeactivateSubject } from '@/components/subjectDetails/deactivateSubject';

export const Route = createFileRoute('/_app/subjects/$subjectId/')({
  loader: ({ context: { queryClient }, params: { subjectId } }) => {
    return queryClient.ensureQueryData(getSubjectQueryOptions(subjectId));
  },
  errorComponent: ErrorComponent,
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const params = Route.useParams();
  const { subjectId } = params as { subjectId: string };
  const { data: subject } = useSuspenseQuery(getSubjectQueryOptions(subjectId));

  return (
    <section className="flex flex-col flex-1">
      <header className="flex flex-col space-y-space-2xs">
        <BackButton linkLabel={ t('routes.patientDetails') } href={ `/patients/${subject.patient.id}` } />
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-space-lg">
            <h1 className="font-h1">
              { subject.eligibilityStatus.protocol.name }
            </h1>
            <SubjectStatus isActive={ subject.isActive } />
          </div>
          <p className="font-normal">
            { `${t('mrn', { ns: 'patients' })}: ${subject.patient.externalId}` }
          </p>
        </div>
      </header>

      <div className="flex flex-col gap-space-lg py-space-2xl">
        <BasicInformation subjectDetails={ subject } readOnly={ !subject.isActive } />
        <ProtocolDetails subject={ subject } />
        <ConsentsTable
          patientDetails={ subject.patient }
          subjectId={ subjectId }
          readOnly={ !subject.isActive }
          protocolName={ subject.eligibilityStatus.protocol.name }
        />
        { subject.isActive && (
          <DeactivateSubject
            subjectId={ subjectId }
            patientId={ subject.patient.id }
            siteId={ subject.patient.site.id }
          />
        ) }
      </div>
    </section>
  );
}
